import * as THREE from 'three';

/**
 * RoadRenderer - Erstellt realistische Straßen mit Fahrbahn, Linien und Texturen
 */
export class RoadRenderer {
  private scene: THREE.Scene;
  private roadGroup: THREE.Group;
  private roadMaterial: THREE.Material;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.roadGroup = new THREE.Group();
    this.roadGroup.name = 'RoadRenderer';
    this.scene.add(this.roadGroup);
    
    // Realistische Straßen-Textur: Asphalt-Look
    this.roadMaterial = new THREE.MeshStandardMaterial({
      color: 0x333333,
      metalness: 0.1,
      roughness: 0.8,
      side: THREE.DoubleSide
    });
  }

  /**
   * Zeichnet eine einfache Rennstrecke mit realistischen Eigenschaften
   */
  createTrack(): void {
    // Hauptfahrbahn (Asphalt)
    const trackWidth = 15; // 15 Einheiten breit = realistische Breite
    const trackLength = 120;
    const trackShape = new THREE.Shape();
    
    // Rechteck für die Fahrbahn
    trackShape.moveTo(-trackWidth / 2, 0);
    trackShape.lineTo(trackWidth / 2, 0);
    trackShape.lineTo(trackWidth / 2, trackLength);
    trackShape.lineTo(-trackWidth / 2, trackLength);
    trackShape.closePath();

    // Kurven-Abschnitte hinzufügen (oval track)
    const geometry = new THREE.ShapeGeometry(trackShape);
    const roadMesh = new THREE.Mesh(geometry, this.roadMaterial);
    roadMesh.rotation.x = -Math.PI / 2;
    roadMesh.position.y = 0;
    this.roadGroup.add(roadMesh);

    // Fahrbahnmarkierungen (weiße Linien)
    this.addRoadMarkings(trackWidth, trackLength);
    
    // Gras-Bereich um die Strecke
    this.addGrassAreas(trackWidth, trackLength);
  }

  private addRoadMarkings(trackWidth: number, trackLength: number): void {
    const markMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 0,
      roughness: 0.9
    });

    // Mittellinie (gestrichelt)
    const lineSpacing = 3;
    for (let i = 0; i < trackLength; i += lineSpacing * 2) {
      const lineGeometry = new THREE.PlaneGeometry(0.5, lineSpacing);
      const lineMesh = new THREE.Mesh(lineGeometry, markMaterial);
      lineMesh.rotation.x = -Math.PI / 2;
      lineMesh.position.set(0, 0.01, i);
      this.roadGroup.add(lineMesh);
    }

    // Randlinie (kontinuierlich)
    this.createBordeline(-trackWidth / 2, trackLength, markMaterial);
    this.createBordeline(trackWidth / 2, trackLength, markMaterial);
  }

  private createBordeline(xPos: number, length: number, material: THREE.Material): void {
    const lineGeometry = new THREE.PlaneGeometry(0.3, length);
    const lineMesh = new THREE.Mesh(lineGeometry, material);
    lineMesh.rotation.x = -Math.PI / 2;
    lineMesh.position.set(xPos, 0.02, length / 2);
    this.roadGroup.add(lineMesh);
  }

  private addGrassAreas(trackWidth: number, trackLength: number): void {
    const grassMaterial = new THREE.MeshStandardMaterial({
      color: 0x2d5016,
      metalness: 0,
      roughness: 0.95
    });

    // Rasenflächen links und rechts
    const grassWidth = 50;
    const grassGeometry = new THREE.PlaneGeometry(grassWidth, trackLength + 30);
    
    const grassLeftMesh = new THREE.Mesh(grassGeometry, grassMaterial);
    grassLeftMesh.rotation.x = -Math.PI / 2;
    grassLeftMesh.position.set(-trackWidth / 2 - grassWidth / 2, -0.1, trackLength / 2);
    this.roadGroup.add(grassLeftMesh);

    const grassRightMesh = new THREE.Mesh(grassGeometry, grassMaterial);
    grassRightMesh.rotation.x = -Math.PI / 2;
    grassRightMesh.position.set(trackWidth / 2 + grassWidth / 2, -0.1, trackLength / 2);
    this.roadGroup.add(grassRightMesh);
  }

  /**
   * Löscht die aktuelle Strecke
   */
  clearTrack(): void {
    // Alle Kinder aus der Gruppe entfernen
    while (this.roadGroup.children.length > 0) {
      const child = this.roadGroup.children[0];
      this.roadGroup.remove(child);
      if ((child as THREE.Mesh).geometry) {
        (child as THREE.Mesh).geometry.dispose();
      }
    }
  }
  
  /**
   * Zeigt/Versteckt die Strecke
   */
  setVisible(visible: boolean): void {
    this.roadGroup.visible = visible;
  }
}
